import { Linkedin, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import {
  BRAND_LOGO_URL,
  CONTACT_EMAIL,
  NAV_HASHES,
  SOCIALS,
} from "@/data/landing";

const ICON_MAP = {
  linkedin: Linkedin,
  instagram: Instagram,
};

export default function Footer() {
  const { t } = useI18n();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-neutral-950 text-stone-200 py-24 sm:py-32 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 grain opacity-[0.35] mix-blend-screen" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl overflow-hidden bg-neutral-900 ring-1 ring-neutral-800">
              <img
                src={BRAND_LOGO_URL}
                alt={t("brand.name") + " logo"}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold tracking-tight text-stone-50">
                {t("brand.name")}
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-400">
                {t("brand.kicker")}
              </div>
            </div>
          </div>

          <p className="mt-6 max-w-sm text-sm text-stone-400 leading-relaxed">
            {t("footer.tagline")}
          </p>

          <div className="mt-8 flex items-center gap-2">
            {SOCIALS.map((s) => {
              const Icon = ICON_MAP[s.icon];
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={s.testid}
                  aria-label={s.label}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-neutral-800 bg-neutral-900 hover:bg-neutral-800 hover:-translate-y-0.5 transition"
                >
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-400">
            {t("footer.productKicker")}
          </div>
          <ul className="mt-5 space-y-3 text-sm">
            {NAV_HASHES.map((l) => (
              <li key={l.key}>
                <a
                  href={l.href}
                  data-testid={`footer-link-${l.key}`}
                  className="text-stone-300 hover:text-stone-50 transition"
                >
                  {t(`nav.${l.key}`)}
                </a>
              </li>
            ))}
            <li>
              <a
                href="/#download"
                className="text-stone-300 hover:text-stone-50 transition"
                data-testid="footer-link-download"
              >
                {t("nav.cta")}
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-400">
            {t("footer.companyKicker")}
          </div>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <Link
                to="/privacy"
                data-testid="footer-link-privacy"
                className="text-stone-300 hover:text-stone-50 transition"
              >
                {t("footer.privacy")}
              </Link>
            </li>
            <li>
              <Link
                to="/terms"
                data-testid="footer-link-terms"
                className="text-stone-300 hover:text-stone-50 transition"
              >
                {t("footer.terms")}
              </Link>
            </li>
            <li>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="footer-link-contact"
                className="text-stone-300 hover:text-stone-50 transition"
              >
                {t("footer.contact")} · {CONTACT_EMAIL}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="relative mt-20 pt-8 border-t border-neutral-900 mx-auto max-w-7xl px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-stone-500">
        <div data-testid="footer-copyright">
          © {new Date().getFullYear()} {t("brand.name")}. {t("footer.copyright")}
        </div>
        <div data-testid="footer-made-by">
          {t("footer.madeBy")}{" "}
          <a
            href="https://venei.co"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-stone-300 hover:text-stone-50 transition underline-offset-4 hover:underline"
          >
            {t("footer.madeByCompany")}
          </a>{" "}
          — {t("footer.madeWithLove")}
        </div>
      </div>
    </footer>
  );
}
